import moment from 'moment';
import {currencyName} from 'utils/currencyFormatting';

/**
 * The filters the swap page's four operations share. Unset fields are left
 * out — an omitted filter matches everything — and a date range picked as
 * local calendar days becomes that range's full span in UTC: the first day
 * from 00:00:00.000 and the last up to 23:59:59.999, inclusive.
 */
export const buildSwapFilters = ({
  sourceCurrency,
  targetCurrency,
  status,
  dates,
} = {}) => {
  const [from, to] = dates || [];
  return {
    ...(sourceCurrency && {sourceCurrency}),
    ...(targetCurrency && {targetCurrency}),
    ...(status && {status}),
    ...(from && {startDate: moment(from).startOf('day').toISOString()}),
    ...(to && {endDate: moment(to).endOf('day').toISOString()}),
  };
};

/** The filters in words, for the bulk confirmation and the export toast. */
export const describeSwapFilters = ({
  sourceCurrency,
  targetCurrency,
  dates,
}) => {
  const [from, to] = dates || [];
  const day = value => moment(value).format('D MMM YYYY');
  const parts = [
    sourceCurrency && targetCurrency
      ? `${sourceCurrency} → ${targetCurrency}`
      : sourceCurrency
      ? `from ${sourceCurrency} (${currencyName(sourceCurrency)})`
      : targetCurrency
      ? `into ${targetCurrency} (${currencyName(targetCurrency)})`
      : 'in every currency',
    from && to
      ? `created ${day(from)} – ${day(to)}`
      : from
      ? `created from ${day(from)}`
      : to
      ? `created up to ${day(to)}`
      : 'on any date',
  ];
  return parts.join(', ');
};

// Bulk jobs ---------------------------------------------------------------------

export const TERMINAL_JOB_STATUSES = ['COMPLETED', 'COMPLETED_WITH_ERRORS'];
export const isJobTerminal = job => TERMINAL_JOB_STATUSES.includes(job?.status);

export const JOB_STATUS = {
  QUEUED: {label: 'Queued', color: 'default'},
  RUNNING: {label: 'Running', color: 'processing'},
  COMPLETED: {label: 'Completed', color: 'success'},
  COMPLETED_WITH_ERRORS: {label: 'Completed with errors', color: 'warning'},
};

/** Who started a job: their name, else their email, else the raw id. */
export const jobRequester = job => {
  const user = job?.requestedBy;
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.email || job?.requestedById || 'Unknown admin';
};

/**
 * Starts one bulk fulfil or reject — exactly one request, never repeated.
 *
 * `{job}` is a receipt (usually QUEUED), not proof any swap moved. `{error}` is
 * the backend's refusal: nothing was started. `{error, uncertain: true}` means
 * the request may have reached the backend before failing (a timeout, a lost
 * response), so a job could already be running: the caller must send the
 * admin to their recent jobs rather than offer to submit again.
 */
export const submitBulkSwapJob = async ({mutate, input}) => {
  let result;
  try {
    const {data} = await mutate({variables: {input}});
    result = data?.bulkFulfilOrRejectPendingSwapTransactions;
  } catch (error) {
    return {error: error?.message || 'The request failed.', uncertain: true};
  }
  if (result?.__typename === 'BulkSwapTransactionJob') return {job: result};
  if (result?.__typename === 'Error') return {error: result.message};
  return {error: 'The backend did not confirm the job.', uncertain: true};
};

// Bulk rate updates --------------------------------------------------------------

// The backend refuses more than this many selected IDs for a rate update;
// a larger set goes through "all matching", which it caps separately.
export const MAX_RATE_SELECTED_IDS = 20;
export const MAX_RATE_ALL_MATCHING = 5000;

/**
 * The one currency pair a selection shares — a single rate only means
 * something for one pair — and the spread of rates it holds today.
 * `mixed` is true when the rows span more than one pair.
 */
export const describeRateSelection = rows => {
  const pairs = {};
  const rates = [];
  (rows || []).forEach(row => {
    const source = row?.sourceCurrency?.currency ?? row?.sourceCurrency;
    const target = row?.targetCurrency?.currency ?? row?.targetCurrency;
    if (source && target) {
      pairs[`${source}/${target}`] = {
        sourceCurrency: source,
        targetCurrency: target,
      };
    }
    if (Number.isFinite(Number(row?.rate))) rates.push(Number(row.rate));
  });
  const found = Object.values(pairs);
  return {
    pair: found.length === 1 ? found[0] : null,
    mixed: found.length > 1,
    minRate: rates.length ? Math.min(...rates) : undefined,
    maxRate: rates.length ? Math.max(...rates) : undefined,
  };
};

/**
 * Writes one rate to many pending swaps. Unlike a bulk fulfil this runs inline
 * and is idempotent — the same rate sent twice lands the same — so a lost
 * response may simply be sent again.
 */
export const submitBulkSwapRate = async ({mutate, input}) => {
  let result;
  try {
    const {data} = await mutate({variables: {input}});
    result = data?.bulkUpdateSwapTransactionRate;
  } catch (error) {
    return {error: error?.message || 'The request failed.'};
  }
  if (result?.__typename === 'BulkSwapRateUpdateResult') return {result};
  return {error: result?.message || 'The backend did not confirm the update.'};
};

// From a million up the full figure no longer fits a card, so it shortens.
const COMPACT_FROM = 1e6;
const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
});
const fullUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A card's USD amount: `short` always fits ("$1.24M"), `full` is the exact
 * figure for the tooltip. Below a million the two are the same.
 */
export const cardUsd = value => {
  const amount = Number(value) || 0;
  const full = fullUsd.format(amount);
  return {
    full,
    short: Math.abs(amount) >= COMPACT_FROM ? compactUsd.format(amount) : full,
  };
};
